import { useState } from "react";

import { useMediaQuery } from "react-responsive";
import { useNavigate, useParams } from "react-router";

import { SEOHead } from "@/components/helpers/seo/SEOHead";
import { Box, Flex } from "@/components/layout/flexbox";
import { breakpoints } from "@/components/layout/Media";
import { Nav } from "@/components/nav/Nav";
import { Authenticate } from "@/components/pages_logged_in/Authenticate";
import { ClientNav } from "@/components/pages_logged_in/workspace/ClientNav/ClientNav";
import {
  ClientWorkspaceProvider,
  useClientWorkspaceContext,
} from "@/components/pages_logged_in/workspace/clients/ClientWorkspaceContext";
import { ProfileSidebar } from "@/components/pages_logged_in/workspace/profile/ProfileSidebar";
import { CoachingDashboard } from "@/components/pieces/coaching/CoachingDashboard";
import {
  SIDE_NAV_WIDTH,
  SITE_NAME,
  STANDARD_NAV_HEIGHT,
} from "@/css/constants";
import { useCustomerProfileQuery } from "@/graphql";
import { CustomerUser } from "@/types/users";

export function ClientProfile({ customerId }: { customerId: string }) {
  const navigate = useNavigate();

  const mobile = useMediaQuery({
    query: `(max-width: ${breakpoints.md}px)`,
  });

  const [detailsVisible, setDetailsVisible] = useState(true);
  const [notesVisible, setNotesVisible] = useState(true);
  const [intakeFormVisible, setIntakeFormVisible] = useState(false);

  const { currentWorkspaceId, workspaces, onSelectWorkspace } =
    useClientWorkspaceContext();

  const { data, loading } = useCustomerProfileQuery({
    variables: {
      id: customerId,
      workspaceId: currentWorkspaceId,
    },
    fetchPolicy: "network-only",
    onError: (data) => {
      if (data.message.includes("Forbidden")) {
        navigate("/clients", {
          replace: true,
        });
      }
    },
  });

  const customer: CustomerUser = data?.customer || {
    id: "",
    name: "",
    email: "",
    photo: "",
    coachAssignments: [],
    phoneNumber: "",
    timeZone: "",
  };

  return (
    <Box ml={[0, 0, SIDE_NAV_WIDTH]}>
      <SEOHead title={`${customer.name} - ${SITE_NAME}`} />
      <Box>
        <ClientNav
          client={customer}
          currentWorkspaceId={currentWorkspaceId}
          workspaces={workspaces}
          onSelectWorkspace={onSelectWorkspace}
        />
      </Box>
      <Flex
        flexDirection={mobile ? "column" : "row"}
        style={{ minHeight: `calc(100vh - ${STANDARD_NAV_HEIGHT * 2}px)` }}
      >
        <Box style={{ flex: 1 }} py={3} px={[3, 3, 4]}>
          <div style={{ maxWidth: 800 }}>
            <Box mb={[3, 3, 4, 5]}>
              <CoachingDashboard
                customerId={customerId}
                viewAsCoach={true}
                currentWorkspaceId={currentWorkspaceId}
              />
            </Box>
          </div>
        </Box>
        <ProfileSidebar
          loading={loading}
          user={customer}
          notesVisible={notesVisible}
          setNotesVisible={setNotesVisible}
          detailsVisible={detailsVisible}
          setDetailsVisible={setDetailsVisible}
          intakeFormVisible={intakeFormVisible}
          setIntakeFormVisible={setIntakeFormVisible}
          mobile={mobile}
        />
      </Flex>
    </Box>
  );
}

export function ClientProfilePage() {
  const navigate = useNavigate();
  const { userId: customerId } = useParams();

  if (!customerId) {
    navigate("/clients", { replace: true });
    return null;
  }

  return (
    <Authenticate requireCoach={true} layout="dashboard" maxWidth="100%">
      <Nav />
      <ClientWorkspaceProvider clientId={customerId}>
        <ClientProfile customerId={customerId} />
      </ClientWorkspaceProvider>
    </Authenticate>
  );
}
